import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { 
  ShoppingCart, Star, Check, Minus, Plus, ArrowLeft, 
  Truck, Shield, RefreshCcw, Loader2, ChevronRight, Lock 
} from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { fbTrack } from '../lib/facebookPixel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [addingToCart, setAddingToCart] = useState(false);
  const { addToCart } = useCart();

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API}/products/${id}`);
        setProduct(response.data);
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  useEffect(() => {
    if (!product) return;
    fbTrack('ViewContent', {
      content_ids: [String(product.id)],
      content_type: 'product',
      content_name: product.name,
      value: product.price,
      currency: 'INR',
    });
  }, [product]);

  const handleAddToCart = async () => {
    setAddingToCart(true);
    const success = await addToCart(product.id, quantity);
    if (success) {
      fbTrack('AddToCart', {
        content_ids: [String(product.id)],
        content_type: 'product',
        content_name: product.name,
        value: product.price * quantity,
        currency: 'INR',
        num_items: quantity,
      });
      toast.success(`${product.name} added to cart!`);
    } else {
      toast.error('Failed to add to cart');
    }
    setAddingToCart(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-400" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <p className="text-zinc-400 text-lg mb-4">Product not found</p>
        <Link to="/products" className="text-teal-400 hover:underline">
          Back to products
        </Link>
      </div>
    );
  }

  const discount = product.original_price 
    ? Math.round((1 - product.price / product.original_price) * 100) 
    : 0;

  return (
    <div data-testid="product-detail-page" className="min-h-screen pt-[132px] pb-16 px-4 md:px-8">
      <div className="max-w-[1400px] mx-auto">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-zinc-400 mb-8">
          <Link to="/" className="hover:text-white transition-colors">Home</Link>
          <ChevronRight className="w-4 h-4" />
          <Link to="/products" className="hover:text-white transition-colors">Products</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-white">{product.name}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Images */}
          <div className="space-y-4">
            {/* Main Image */}
            <div className="aspect-square bg-zinc-900 border border-zinc-800 overflow-hidden relative">
              {discount > 0 && (
                <div className="absolute top-4 left-4 z-10 bg-teal-400 text-black font-heading font-bold text-sm uppercase tracking-wider px-4 py-2 skew-x-[-10deg]">
                  <span className="skew-x-[10deg] inline-block">{discount}% OFF</span>
                </div>
              )}
              <img 
                src={product.images[selectedImage] || product.image}
                alt={product.name}
                data-testid="product-main-image"
                className="w-full h-full object-cover"
              />
            </div>

            {/* Thumbnail Images */}
            {product.images.length > 1 && (
              <div className="flex gap-4">
                {product.images.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    data-testid={`product-thumbnail-${index}`}
                    className={`w-20 h-20 bg-zinc-900 border overflow-hidden transition-all duration-300 ${
                      selectedImage === index 
                        ? 'border-teal-400 neon-border' 
                        : 'border-zinc-800 hover:border-zinc-600'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-8">
            {/* Header */}
            <div>
              <span className="font-mono text-xs uppercase tracking-widest text-teal-400">
                {product.category.replace('-', ' ')}
              </span>
              <h1 
                data-testid="product-name"
                className="font-heading text-4xl md:text-5xl font-black uppercase tracking-tighter mt-2"
              >
                {product.name}
              </h1>
              
              {/* Rating */}
              <div className="flex items-center gap-3 mt-4">
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star 
                      key={i} 
                      className={`w-5 h-5 ${i < Math.floor(product.rating) ? 'fill-teal-400 text-teal-400' : 'text-zinc-700'}`} 
                    />
                  ))}
                </div>
                <span className="text-zinc-400">
                  {product.rating} ({product.reviews_count.toLocaleString()} reviews)
                </span>
              </div>
            </div>

            {/* Price */}
            <div className="flex items-baseline gap-4">
              <span 
                data-testid="product-price"
                className="font-heading text-5xl font-black"
              >
                ₹{product.price.toLocaleString()}
              </span>
              {product.original_price && (
                <span className="text-zinc-500 line-through text-2xl">
                  ₹{product.original_price.toLocaleString()}
                </span>
              )}
            </div>

            {/* Description */}
            <p className="text-zinc-300 text-lg leading-relaxed">
              {product.description}
            </p>

            {/* Features */}
            <div>
              <h3 className="font-heading text-xl font-bold uppercase tracking-tight mb-4">Key Features</h3>
              <ul className="space-y-3">
                {product.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-teal-400 mt-0.5 flex-shrink-0" />
                    <span className="text-zinc-300">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Quantity & Add to Cart */}
            <div className="flex flex-wrap gap-4 items-center">
              {/* Quantity */}
              <div className="flex items-center border border-zinc-700">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  data-testid="quantity-decrease"
                  className="p-3 hover:bg-zinc-800 transition-colors"
                >
                  <Minus className="w-5 h-5" />
                </button>
                <span 
                  data-testid="quantity-value"
                  className="w-16 text-center font-heading text-xl font-bold"
                >
                  {quantity}
                </span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  data-testid="quantity-increase"
                  className="p-3 hover:bg-zinc-800 transition-colors"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>

              {/* Add to Cart */}
              <Button
                onClick={handleAddToCart}
                disabled={addingToCart || !product.in_stock}
                data-testid="add-to-cart-btn"
                className="flex-1 h-14 bg-teal-400 text-black hover:bg-white font-heading font-black uppercase tracking-widest skew-x-[-10deg] text-lg group disabled:opacity-50"
              >
                <span className="skew-x-[10deg] flex items-center justify-center gap-2">
                  {addingToCart ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <ShoppingCart className="w-5 h-5" />
                  )}
                  {product.in_stock ? 'Add to Cart' : 'Out of Stock'}
                </span>
              </Button>
            </div>

            {/* Trust Badges */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-6 border-t border-zinc-800">
              <div className="text-center">
                <Truck className="w-6 h-6 text-teal-400 mx-auto mb-2" />
                <span className="text-xs text-zinc-400 uppercase tracking-wide">Free Shipping</span>
              </div>
              <div className="text-center">
                <RefreshCcw className="w-6 h-6 text-teal-400 mx-auto mb-2" />
                <span className="text-xs text-zinc-400 uppercase tracking-wide">30-Day Returns</span>
              </div>
              <div className="text-center">
                <Shield className="w-6 h-6 text-teal-400 mx-auto mb-2" />
                <span className="text-xs text-zinc-400 uppercase tracking-wide">1-Year Warranty</span>
              </div>
              <div className="text-center">
                <Lock className="w-6 h-6 text-teal-400 mx-auto mb-2" aria-hidden />
                <span className="text-xs text-zinc-400 uppercase tracking-wide">Secure Payment</span>
              </div>
            </div>

            {/* Specs */}
            <div className="glass-panel p-6">
              <h3 className="font-heading text-xl font-bold uppercase tracking-tight mb-4">Specifications</h3>
              <dl className="grid grid-cols-2 gap-4">
                {Object.entries(product.specs).map(([key, value]) => (
                  <div key={key}>
                    <dt className="text-zinc-500 text-sm">{key}</dt>
                    <dd className="font-medium">{value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
